import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography
} from '@mui/material';
import {useSession} from '../../../../firebase/UserProvider';
import {useAccessSubscription} from '../../../../firebase/realtime_database/useAccessSubscription';
import {removeUserAccess, setUserAccess} from '../../../../firebase/realtime_database/access';
import {
    getAllUsers,
    getUserDoc,
    getUsersByIds,
    normalizeApiUser,
    searchUsersByName,
} from '../../../../firebase/firestore/users';

// New modular components
import SearchUsers from './SearchUsers';
import AccessList from './AccessList';

export default function ShareDialog({open, onClose, reportId}) {
    const {user: currentUser} = useSession();
    const currentUserId = currentUser?.uid || currentUser?.id || null;

    // Subscribe to full access map for the report
    const {value: accessMapLive, isLoading: accessLoading} = useAccessSubscription(reportId);
    const accessMap = React.useMemo(() => accessMapLive || {}, [accessMapLive]);

    const [usersById, setUsersById] = React.useState({}); // { userId: userDoc }

    // search state
    const [query, setQuery] = React.useState('');
    const [debouncing, setDebouncing] = React.useState(false);
    const [searchLoading, setSearchLoading] = React.useState(false);
    const [results, setResults] = React.useState([]);
    const [searchError, setSearchError] = React.useState(null);

    // Fetch user docs for ids present in accessMap
    React.useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const ids = Object.keys(accessMap || {});
        if (ids.length === 0) {
            setUsersById({});
            return;
        }
        (async () => {
            try {
                const users = await getUsersByIds(ids);
                if (cancelled) return;
                const dict = Object.fromEntries(users.map((u) => [u.id, u]));
                // Ensure each access row has a profile (batch can miss; refresh owner/current user).
                await Promise.all(
                    ids
                        .filter((id) => !dict[id])
                        .map(async (id) => {
                            const doc = await getUserDoc(id);
                            if (doc) dict[id] = doc;
                        })
                );
                if (currentUserId && ids.includes(currentUserId)) {
                    const sessionProfile = normalizeApiUser({
                        userId: currentUserId,
                        id: currentUserId,
                        email: currentUser?.email,
                        displayName: currentUser?.displayName,
                        firstName: currentUser?.firstName,
                        lastName: currentUser?.lastName,
                        hasAvatar: currentUser?.hasAvatar,
                        profilePicture: currentUser?.profilePicture,
                    });
                    const apiProfile = dict[currentUserId] || await getUserDoc(currentUserId);
                    dict[currentUserId] = {...sessionProfile, ...apiProfile};
                }
                // Always refresh owner row from API (avatar may exist only in users table).
                const ownerId = Object.entries(accessMap).find(
                    ([, role]) => (role || '').toLowerCase() === 'owner'
                )?.[0];
                if (ownerId) {
                    const ownerDoc = await getUserDoc(ownerId);
                    if (ownerDoc) dict[ownerId] = {...dict[ownerId], ...ownerDoc};
                }
                if (cancelled) return;
                setUsersById(dict);
            } catch (e) {
                console.error('Failed to fetch users for access map', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, accessMap, currentUserId, currentUser]);

    // Debounced search with debounce indicator and keyboard navigation
    React.useEffect(() => {
        if (!open) return;
        const q = (query || '').trim();

        let cancelled = false;
        setDebouncing(Boolean(q));
        setSearchError(null);

        const load = async () => {
            setSearchLoading(true);
            try {
                const found = q
                    ? await searchUsersByName(q, {limit: 20})
                    : await getAllUsers(50);
                if (cancelled) return;
                const filtered = found.filter(
                    (u) => u?.id && !Object.prototype.hasOwnProperty.call(accessMap, u.id)
                );
                setResults(filtered);
                setSearchError(null);
            } catch (e) {
                if (!cancelled) {
                    setResults([]);
                    setSearchError('Could not load users. Try again.');
                    console.error('ShareDialog user search failed', e);
                }
            } finally {
                if (!cancelled) {
                    setSearchLoading(false);
                    setDebouncing(false);
                }
            }
        };

        const t = q ? setTimeout(load, 300) : load();

        return () => {
            cancelled = true;
            if (typeof t === 'number') clearTimeout(t);
        };
    }, [open, query, accessMap]);

    const handleAddUser = async (userObj) => {
        if (!userObj || !userObj.id) return;
        try {
            await setUserAccess(reportId, userObj.id, 'viewer');
            setQuery('');
            setResults([]);
            setSearchError(null);
        } catch (e) {
            console.error('Failed to add user access', e);
        }
    };

    const handleChangeRole = async (userId, newRole) => {
        if (!userId) return;
        const currentRole = (accessMap[userId] || '').toLowerCase();
        if (currentRole === 'owner') return;
        try {
            await setUserAccess(reportId, userId, newRole);
        } catch (e) {
            console.error('Failed to change user role', e);
        }
    };

    const handleRemoveAccess = async (userId) => {
        if (!userId) return;
        const role = (accessMap[userId] || '').toLowerCase();
        if (role === 'owner') return;
        if (userId === currentUserId) return;
        try {
            await removeUserAccess(reportId, userId);
        } catch (e) {
            console.error('Failed to remove user access', e);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Share report</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    <Stack>
                        <Typography variant="subtitle2" gutterBottom>Add people</Typography>
                        <SearchUsers
                            query={query}
                            setQuery={setQuery}
                            searchLoading={searchLoading || debouncing}
                            results={results}
                            searchError={searchError}
                            onAddUser={handleAddUser}
                        />
                    </Stack>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>People with access</Typography>
                        {accessLoading ? (
                            <Stack alignItems="center" justifyContent="center" sx={{py: 3}}>
                                <CircularProgress/>
                            </Stack>
                        ) : (
                            <AccessList
                                accessMap={accessMap}
                                usersById={usersById}
                                currentUserId={currentUserId}
                                accessLoading={accessLoading}
                                onChangeRole={handleChangeRole}
                                onRemoveAccess={handleRemoveAccess}
                            />
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Done</Button>
            </DialogActions>
        </Dialog>
    );
}
